<script>
  import Section from '../components/Section.svelte';
  import Panel from '../components/Panel.svelte';
  import Alert from '../components/Alert.svelte';
  import Button from '../components/Button.svelte';
  import DataTable from '../components/DataTable.svelte';
  import Async from '../components/Async.svelte';
  import HelpDrawer from '../components/HelpDrawer.svelte';
  import Code from '../components/Code.svelte';
  import { getIngestPreview, getRecords, getResource, getSource, getSources, runIngest } from '../lib/api.js';
  import { SELF } from '../../shared/config.js';
  import { HELP } from '../lib/help.js';
  import { nav, go } from '../lib/nav.svelte.js';

  // Bump to re-read the preview (watermark, pending, record count) after a run.
  let reloadKey = $state(0);
  let sourceId = $state(nav.params.source ?? null);
  let sourceChosen = $state(Boolean(nav.params.source));
  const preview = $derived.by(() => { reloadKey; return loadPreview(); });

  async function loadPreview() {
    const { sources } = await getSources();
    if (!sources.length) return { empty: true };
    if (!sourceId || !sources.some((source) => source.id === sourceId)) {
      sourceId = sources[0].id;
      sourceChosen = sources.length === 1;
    }
    const [ingest, page, mapping] = await Promise.all([
      getIngestPreview(sourceId), getRecords({ sourceId, limit: 5 }), getSource(sourceId).then((source) => source.mapping),
    ]);
    const first = page.rows[0];
    const ttl = first ? (await getResource(first.documentUri ?? first.uri)).ttl : '(no records)';
    return { empty: false, sources, ingest, rows: page.rows, total: page.total, mapping, ttl };
  }

  let running = $state(false);
  let progress = $state(null); // { done, total, message }
  let result = $state(null);
  let error = $state(null);

  async function run() {
    running = true; error = null; result = null;
    progress = { done: 0, total: 0, message: 'Starting…' };
    try {
      result = await runIngest(sourceId, { onProgress: (job) => { if (job.progress) progress = job.progress; } });
      reloadKey += 1; // refresh the preview to reflect the new watermark + records
    } catch (e) {
      error = e.message;
    } finally {
      running = false;
    }
  }

  async function runAll() {
    running = true; error = null; result = null;
    progress = { done: 0, total: 0, message: 'Starting all sources…' };
    try {
      result = await runIngest('all', { onProgress: (job) => { if (job.progress) progress = job.progress; } });
      reloadKey += 1;
    } catch (e) { error = e.message; }
    finally { running = false; }
  }

  const barPct = $derived(progress && progress.total ? (progress.done / progress.total) * 100 : 0);
</script>

<h1 class="wf2-title wf2-title-level-1">Run ingest</h1>
<p class="wf2-subtitle">Pull source rows newer than the watermark into your Pod, then recompute private aggregates</p>

<Async promise={preview} loading="Reading the connector state from your Pod…">
  {#snippet children(p)}
    {#if p.empty}
      <Alert title="No source configured" variant="info"><p>Import and map a source before running ingest.</p></Alert>
      <Button variant="primary" to="connector">Import your first dataset</Button>
    {:else}
    <HelpDrawer {...HELP['connector/run']}>
      {#snippet children()}
        <p class="help-prose">One private batch document as stored (Turtle):</p>
        <Code lang="turtle" text={p.ttl} />
      {/snippet}
    </HelpDrawer>

    <Section title="Connector state">
      <Panel>
        <label class="wf2-field">Source
          <select class="wf2-control" bind:value={sourceId} onchange={() => { sourceChosen = true; result = null; go('connector/run', { source: sourceId }); reloadKey += 1; }}>
            {#each p.sources as source}<option value={source.id}>{source.label}</option>{/each}
          </select>
        </label>
        <DataTable
          columns={['Field', 'Value']}
          rows={[
            [p.ingest.mode === 'snapshot' ? 'Last checked' : 'Read up to (watermark)', p.ingest.watermark ?? '— (never run)'],
            ['Rows in source', `${p.ingest.rowsInSource}`],
            [p.ingest.mode === 'snapshot' ? 'Rows in current snapshot' : 'Not read yet', `${p.ingest.pending}`],
            ['Records in Pod', `${p.ingest.records}`],
            ['Target containers', [...new Set(Object.values(p.mapping.records).map((r) => SELF.podBase + r.container))].join(', ')],
          ]}
        />

        {#if p.ingest.pending > 0}
          <Alert title={p.ingest.mode === 'snapshot' ? `${p.ingest.pending} rows in snapshot` : `${p.ingest.pending} rows ready to ingest`} variant="info">
            <p class="wf2-text">
              Running ingest writes these records to your Pod and counts your
              private cubes again. It does <strong>not</strong> touch the cubes
              your partners read — letting them see a new quarter is a separate,
              deliberate act by the governance owner (Partners → “Update shared
              cubes”).
            </p>
          </Alert>
        {:else}
          <Alert title="Up to date" variant="success">
            <p class="wf2-text">
              Every source row up to the watermark is already in your Pod. Running
              ingest now is a no-op.
            </p>
          </Alert>
        {/if}

        {#if progress}
          <div class="wf2-tablewrap" style="margin: var(--space-3) 0">
            <p class="wf2-caption">{progress.message}{progress.total ? ` — ${progress.done}/${progress.total}` : ''}</p>
            <div class="bar-track"><div class="bar" style="width:{barPct}%"></div></div>
          </div>
        {/if}

        {#if error}
          <Alert title="Ingest failed" variant="danger"><p class="wf2-text">{error}</p></Alert>
        {/if}

        {#if result}
          <Alert title="{result.sourceLabel} ingest complete" variant="success">
            <p class="wf2-text">
              {#if result.results}
                Ingested {result.ingested} records across {result.results.length} sources{result.failed ? `, ${result.failed} failed` : ''}.
              {:else}
              Mode {result.mode}. Read {result.pending} pending row(s) and rebuilt
              {result.ingested} source record(s){result.failed ? `, ${result.failed} failed` : ''}.
              Source records {result.recordsBefore} → {result.recordsAfter}.
              {#if result.newWatermark}Watermark now {result.newWatermark}.{/if}
              {/if}
            </p>
            {#if result.results}<DataTable columns={['Source', 'Mode', 'Rows', 'Ingested', 'Failed']} rows={result.results.map((item) => [item.sourceLabel, item.mode, `${item.rowsInSource}`, `${item.ingested}`, `${item.failed}`])} />{/if}
          </Alert>
        {/if}

        <div class="wf2-row" style="gap:12px">
          <Button variant="primary" onclick={run} disabled={running || !sourceChosen}>
            {running ? 'Running…' : 'Run ingest'}
          </Button>
          {#if p.sources.length > 1}<Button variant="secondary" onclick={runAll} disabled={running}>Run all sources</Button>{/if}
        </div>
        {#if !sourceChosen}<p class="wf2-hint">Choose a source before running a source-local ingest.</p>{/if}
      </Panel>
    </Section>

    <Section title="Sample records in Pod">
      <Panel>
        <DataTable
          columns={['Record', 'Type', 'Period']}
          rows={p.rows.map((r) => [r.id, r.type, r.period])}
        />
        <p class="wf2-caption">First 5 of {p.total} private records from {p.ingest.sourceLabel}.</p>
      </Panel>
    </Section>

    <div class="wf2-row" style="gap:12px">
      <Button variant="primary" to="dashboard">Done</Button>
      <Button variant="secondary" to="connector">Back</Button>
    </div>
    {/if}
  {/snippet}
</Async>
